import React, { useState, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import SearchIcon from '@mui/icons-material/Search';

const COLUMN_LABELS = { user_name: 'sudo name' };
const HIDDEN_KEYS = ['user_id', 'roles'];
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

function capitalizeHeader(str) {
  return String(str)
    .split(' ')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');
}

function formatCellValue(key, val) {
  if (val == null || val === '') return '—';
  if (key === 'roles' || key === 'role') {
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed.join(', ') : val;
      } catch {
        return val;
      }
    }
    return String(val);
  }
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function AgentReportTable({ rows, title }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rawKeys = Object.keys(rows[0] || {});
  const keys = rawKeys.filter((k) => !HIDDEN_KEYS.includes(k));
  const getLabel = (k) => capitalizeHeader(COLUMN_LABELS[k] ?? String(k).replace(/_/g, ' '));

  const searchLower = (search || '').toLowerCase().trim();
  const filtered = useMemo(() => {
    if (!searchLower) return rows;
    return rows.filter((r) =>
      keys.some((k) => formatCellValue(k, r[k]).toString().toLowerCase().includes(searchLower))
    );
  }, [rows, keys, searchLower]);

  const from = page * rowsPerPage;
  const paginated = useMemo(
    () => filtered.slice(from, from + rowsPerPage),
    [filtered, from, rowsPerPage]
  );

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PhoneCallbackIcon color="primary" /> {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No data found.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneCallbackIcon color="primary" /> {title}
          </Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {keys.map((k) => <TableCell key={k}>{getLabel(k)}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={keys.length} sx={{ textAlign: 'center', py: 3 }}>
                    {rows.length === 0 ? 'No data.' : 'No rows match your search.'}
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((r, i) => (
                  <TableRow key={i}>
                    {keys.map((k) => (
                      <TableCell key={k}>{formatCellValue(k, r[k])}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows per page"
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardAgentCallReports({ agentCallReport }) {
  const hasLeadGen = Array.isArray(agentCallReport?.lead_gen_reports);
  const hasCloser = Array.isArray(agentCallReport?.closer_reports);
  if (!hasLeadGen && !hasCloser) return null;

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {hasLeadGen && (
        <Grid item xs={12} md={12} lg={6} sx={{ minWidth: 0 }}>
          <AgentReportTable rows={agentCallReport.lead_gen_reports} title="Lead Gen Reports" />
        </Grid>
      )}
      {hasCloser && (
        <Grid item xs={12} md={12} lg={6} sx={{ minWidth: 0 }}>
          <AgentReportTable rows={agentCallReport.closer_reports} title="Closer Reports" />
        </Grid>
      )}
    </Grid>
  );
}
